import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, uploadFile } from "@/lib/api";
import { colors as C } from "@/lib/theme";
import { Ion } from "@/components/Ion";
import { FormLabel, Sublabel, Help, Input, BtnPrimary, Chip, ChipRow, FilePicker } from "@/components/AdminParts";

const RESOURCE_SECTIONS = ["equipment_manuals", "equipment_pricing", "sales_programs", "safety_resources", "training_videos", "forms", "marketing", "social_media", "brand_standards", "updates"];
const MAX_MB = 25;
const tooBig = (file) => file.size > MAX_MB * 1024 * 1024 && (toast.error(`That file is larger than ${MAX_MB} MB.`), true);

export function ManualTab({ master, reload }) {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [url, setUrl] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const publish = async () => {
    if (!file && !url) return toast.error("Pick a PDF or paste a URL first.");
    setBusy(true);
    try {
      const pdfUrl = file ? (await uploadFile(file)).url : url.trim();
      await api("/manual/master", { method: "POST", body: { file_name: name || file?.name || url.split("/").pop() || "operations_manual.pdf", pdf_url: pdfUrl } });
      toast.success("Operations Manual updated. Franchisees can now view the new PDF.");
      setFile(null); setUrl(""); setName(""); reload();
    } catch (e) { toast.error(e.message || "Upload failed"); } finally { setBusy(false); }
  };
  return (
    <div data-testid="console-manual-tab">
      <FormLabel>OPERATIONS MANUAL — MASTER PDF</FormLabel>
      <Help>Upload the master Operations Manual PDF. Franchisees will see a "View Master Manual" card at the top of the Operations Manual screen — visible only after login. Re-uploading replaces the current file.</Help>
      {master?.has_pdf && (
        <div className="mb-4 flex items-center gap-2 rounded-[14px] border p-4" style={{ background: C.cardElevated, borderColor: C.accent }} data-testid="manual-current">
          <Ion name="document-text" size={22} color={C.accent} />
          <div className="min-w-0 flex-1"><p className="truncate text-[13px] font-bold">{master.file_name}</p>
            <p className="mt-0.5 text-[11px]" style={{ color: C.textSecondary }}>{master.pdf_url ? "Hosted URL" : "Stored in app"}{master.uploaded_at ? ` • ${new Date(master.uploaded_at).toLocaleDateString()}` : ""}</p></div>
          <button onClick={() => navigate("/manual-viewer")} data-testid="manual-view" className="rounded-lg px-3 py-2 text-[11px] font-extrabold tracking-[1.5px] text-black" style={{ background: C.accent }}>VIEW</button>
        </div>
      )}
      <Sublabel>Option 1 — Pick PDF from device</Sublabel>
      <FilePicker accept="application/pdf" testId="manual-pick" label={file ? `Selected: ${file.name}` : "Choose PDF file…"} selected={!!file} onClear={() => setFile(null)}
        onPick={(f) => { if (!tooBig(f)) { setFile(f); setUrl(""); } }} />
      <Sublabel>Option 2 — Paste a hosted URL</Sublabel>
      <Input data-testid="manual-url" placeholder="https://example.com/operations_manual.pdf" value={url} onChange={(e) => { setUrl(e.target.value); if (e.target.value) setFile(null); }} />
      <Input data-testid="manual-filename" placeholder="Display file name (optional)" value={name} onChange={(e) => setName(e.target.value)} />
      <BtnPrimary data-testid="admin-upload-manual" busy={busy} onClick={publish}>{busy ? "UPLOADING…" : master?.has_pdf ? "REPLACE OPERATIONS MANUAL" : "PUBLISH OPERATIONS MANUAL"}</BtnPrimary>
    </div>
  );
}

export function ResourceTab() {
  const [section, setSection] = useState(RESOURCE_SECTIONS[0]);
  const [f, setF] = useState({ title: "", description: "", price: "", url: "" });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const kind = file ? (file.type.includes("pdf") ? "pdf" : "image") : null;
  const pick = (picked) => { if (tooBig(picked)) return; setFile(picked); setPreview(picked.type.includes("pdf") ? null : URL.createObjectURL(picked)); };
  const clear = () => { setFile(null); setPreview(null); };
  const publish = async () => {
    if (!f.title || !f.description) return toast.error("Title and description are required.");
    setBusy(true);
    try {
      const body = { section, title: f.title, description: f.description, price: f.price || null, pdf_url: f.url || null, image_url: null };
      if (file) {
        const up = await uploadFile(file);
        if (kind === "image") body.image_url = up.url;
        body.pdf_url = f.url || up.url;
      }
      await api("/resources", { method: "POST", body });
      toast.success(`Resource added to ${section}.`);
      setF({ title: "", description: "", price: "", url: "" }); clear();
    } catch (e) { toast.error(e.message || "Failed"); } finally { setBusy(false); }
  };
  return (
    <div data-testid="console-resource-tab">
      <FormLabel>UPLOAD A RESOURCE</FormLabel>
      <Help>Add a PDF link, document, or update visible to all franchisees.</Help>
      <Sublabel>Section</Sublabel>
      <ChipRow>{RESOURCE_SECTIONS.map((s) => <Chip key={s} active={section === s} onClick={() => setSection(s)} data-testid={`res-section-${s}`}>{s.replace(/_/g, " ")}</Chip>)}</ChipRow>
      <Input data-testid="res-title" placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
      <Input data-testid="res-desc" multiline placeholder="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
      <Input data-testid="res-price" placeholder="Price (optional, e.g. $1,250)" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} />
      <Sublabel>Upload PDF or image</Sublabel>
      <FilePicker accept="application/pdf,image/jpeg,image/png,image/webp" testId="res-pick" icon={kind === "image" ? "image-outline" : kind === "pdf" ? "document-attach-outline" : "cloud-upload-outline"}
        label={file ? `Selected: ${file.name}` : "Choose PDF, JPG, or PNG…"} selected={!!file} onClear={clear} onPick={pick} />
      {preview && <img src={preview} alt="Selected resource" className="mb-2 h-[180px] w-full rounded-lg bg-black object-cover" data-testid="res-preview" />}
      <Sublabel>Or paste a hosted URL (optional)</Sublabel>
      <Input data-testid="res-url" placeholder="https://… (PDF, image, video, form, etc.)" value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} />
      <BtnPrimary data-testid="admin-upload-resource" busy={busy} onClick={publish}>{busy ? "PUBLISHING…" : "PUBLISH RESOURCE"}</BtnPrimary>
    </div>
  );
}
